import { useQueueWorkflowStore } from "@elixir/stores";
import { TWorkflowView, WORKFLOW_VIEW_POPUP_STATE } from "@elixir/types";
import { PrimaryButton, SecondaryButton } from "ui";
import CreateViewSteps from "../create-view-steps";
import { ArrowLeftRegularIcon, XMarkRegularIcon } from "@elixir/icons";
import usePostCreateWorkflowView from "../../../apis/tryst/workflow-view/use-post-create-workflow-view";
import { Drawer, notification } from "antd";
import { useMemo } from "react";
import { getFilterExpressionFromOptions } from "@elixir/utils";

const CreateViewDrawer = ({ children }: { children?: JSX.Element }) => {
  const popupState = useQueueWorkflowStore(
    (state) => state.createViewPopupState
  );
  const setPopupState = useQueueWorkflowStore(
    (state) => state.setCreateViewPopupState
  );
  const clearSelectedPopupData = useQueueWorkflowStore(
    (state) => state.clearSelectedPopupData
  );
  const viewName = useQueueWorkflowStore((state) => state.viewName);
  const selectedFiltersOptions = useQueueWorkflowStore(
    (state) => state.selectedFiltersOptions
  );
  const { mutateAsync: postCreateWorkflowView } = usePostCreateWorkflowView();

  const onBackClick = () => {
    if (!popupState) {
      return;
    }
    const backState = viewCreationBackState[popupState];
    if (!backState) {
      clearSelectedPopupData();
    }
    setPopupState(backState);
  };
  const onNextClick = async () => {
    if (!popupState) {
      return;
    }

    const nextState = viewCreationNextState[popupState];
    if (!nextState) {
      const filterExpression = getFilterExpressionFromOptions(
        selectedFiltersOptions
      );

      const payload: TWorkflowView = {
        name: viewName.trim(),
        filterExpression,
        includeInAgg: true,
      };
      const { success } = await postCreateWorkflowView(payload);
      if (!success) {
        notification.error({
          message: "Error occured while view creation!",
          placement: "bottomRight",
        });
      } else {
        notification.success({
          message: "Custom view created successfully!",
          placement: "bottomRight",
        });
        setPopupState(nextState);
        clearSelectedPopupData();
      }
      return;
    }
    setPopupState(nextState);
  };

  const handleCloseClick = () => {
    setPopupState(undefined);
    clearSelectedPopupData();
  };

  const filtersDisabled = useMemo(() => {
    return !Object.keys(selectedFiltersOptions).filter((filter) => {
      const { options } = selectedFiltersOptions[filter];
      return !!options && options.length > 0;
    }).length;
  }, [selectedFiltersOptions]);

  return (
    <Drawer
      bodyStyle={{ padding: 0 }}
      closable={false}
      placement={"right"}
      onClose={handleCloseClick}
      open={true}
      width={880}
      extra={null}
      destroyOnClose={true}
      footer={null}
    >
      <div className="dr-relative h-full overflow-hidden">
        <div className="dr-top-0 dr-sticky dr-bg-darwin-touch-neutral-0 z-1 dr-border-b dr-border-darwin-neutral-200">
          <CreateViewSteps />
          <XMarkRegularIcon
            className="dr-absolute dr-top-12 dr-right-12 dr-text-doctor-darwin-neutral-600 dr-w-12 dr-h-14 dr-cursor-pointer"
            onClick={handleCloseClick}
          />
        </div>
        <div
          className="dr-overflow-hidden"
          style={{ height: `calc(100% - 141px)` }}
        >
          {children}
        </div>
        <div className="dr-bottom-0 dr-sticky dr-flex dr-flex-row dr-justify-end dr-gap-8 dr-p-12 dr-border-t dr-border-darwin-neutral-200 dr-w-full dr-bg-darwin-touch-neutral-0">
          <SecondaryButton onClick={onBackClick} title="Back" width={118} />
          {popupState && viewCreationNextState[popupState] ? (
            <PrimaryButton
              onClick={onNextClick}
              IconComponent={() => (
                <ArrowLeftRegularIcon className="dr-rotate-180 dr-w-12 dr-h-14" />
              )}
              title="Next"
              width={118}
              isDisabled={
                popupState === WORKFLOW_VIEW_POPUP_STATE.VIEW_NAMING
                  ? !viewName
                  : popupState === WORKFLOW_VIEW_POPUP_STATE.CONFIRM_FILTERS ||
                      popupState === WORKFLOW_VIEW_POPUP_STATE.APPLY_FILTERS
                    ? filtersDisabled
                    : false
              }
            />
          ) : (
            <PrimaryButton
              onClick={onNextClick}
              title="Save"
              width={118}
              isDisabled={!viewName || filtersDisabled}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CreateViewDrawer;

export const viewCreationBackState: Record<
  WORKFLOW_VIEW_POPUP_STATE,
  WORKFLOW_VIEW_POPUP_STATE | undefined
> = {
  [WORKFLOW_VIEW_POPUP_STATE.APPLY_FILTERS]:
    WORKFLOW_VIEW_POPUP_STATE.VIEW_NAMING,
  [WORKFLOW_VIEW_POPUP_STATE.CONFIGURE_CARD]:
    WORKFLOW_VIEW_POPUP_STATE.CONFIRM_FILTERS,
  [WORKFLOW_VIEW_POPUP_STATE.CONFIRM_FILTERS]:
    WORKFLOW_VIEW_POPUP_STATE.APPLY_FILTERS,
  [WORKFLOW_VIEW_POPUP_STATE.VIEW_NAMING]: undefined,
  [WORKFLOW_VIEW_POPUP_STATE.INTRO]: undefined,
  [WORKFLOW_VIEW_POPUP_STATE.VIEW_QUEUES]: undefined,
  [WORKFLOW_VIEW_POPUP_STATE.EDIT_FILTERS]: undefined,
};

const viewCreationNextState: Record<
  WORKFLOW_VIEW_POPUP_STATE,
  WORKFLOW_VIEW_POPUP_STATE | undefined
> = {
  [WORKFLOW_VIEW_POPUP_STATE.APPLY_FILTERS]:
    WORKFLOW_VIEW_POPUP_STATE.CONFIRM_FILTERS,
  [WORKFLOW_VIEW_POPUP_STATE.CONFIGURE_CARD]: undefined,
  [WORKFLOW_VIEW_POPUP_STATE.CONFIRM_FILTERS]: undefined,
  [WORKFLOW_VIEW_POPUP_STATE.VIEW_NAMING]:
    WORKFLOW_VIEW_POPUP_STATE.APPLY_FILTERS,
  [WORKFLOW_VIEW_POPUP_STATE.INTRO]: WORKFLOW_VIEW_POPUP_STATE.VIEW_NAMING,
  [WORKFLOW_VIEW_POPUP_STATE.VIEW_QUEUES]: undefined,
  [WORKFLOW_VIEW_POPUP_STATE.EDIT_FILTERS]: undefined,
};
